import { combineLatest, merge, Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NotificationToast } from '@core/services/notification/notification';
import { NotificationService } from '@core/services/notification/notification.service';
import { PlatformService } from '@core/services/platform.service';
import { SiteStateService } from '@core/services/site-state/site-state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ScrollService } from '@core/services/scroll/scroll.service';
import { NotificationComponent } from './notification.component';
import { NgFor, NgStyle } from '@angular/common';

@UntilDestroy()
@Component({
    selector: 'app-notifications',
    templateUrl: './notifications.component.html',
    styleUrls: ['./notifications.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgStyle, NgFor, NotificationComponent]
})
export class NotificationsComponent {
    private notificationsStickyOffset$: Observable<number> = combineLatest([
        merge(
            this.scrollService.scrollingDown(100).pipe(startWith(false), untilDestroyed(this)),
            this.router.events
                .pipe(
                    // Reset scrollingDown if navigation has occured
                    filter(event => event instanceof NavigationEnd && this.platformService.isBrowser),
                    map(() => false),
                    untilDestroyed(this)
                )
                .pipe(untilDestroyed(this))
        ),
        this.siteStateService.state$.pipe(
            map(states => states.dialog),
            untilDestroyed(this)
        )
    ]).pipe(
        map(([scrollingDown, dialogActive]) => {
            if (dialogActive || scrollingDown) {
                return 0;
            }
            return this.scrollService.getHeaderHeight();
        })
    );
    public notifications: NotificationToast[] = [];
    public notificationStyle: Partial<CSSStyleDeclaration> = { transform: 'translateY(0)' };

    constructor(
        private readonly notificationService: NotificationService,
        private readonly cd: ChangeDetectorRef,
        private readonly scrollService: ScrollService,
        private readonly siteStateService: SiteStateService,
        private readonly router: Router,
        private readonly platformService: PlatformService
    ) {
        this.notificationService.notification$.subscribe(notification => {
            const overflow = this.notifications.length - 2;
            if (overflow) {
                for (let i = 0; i < overflow; i += 1) {
                    this.notifications[i].initiateClose = true;
                }
            }
            this.notifications.push(notification);
            this.cd.markForCheck();
        });

        this.notificationsStickyOffset$.pipe(untilDestroyed(this)).subscribe(offset => {
            this.notificationStyle = { transform: `translateY(${offset}px)` };
            this.cd.markForCheck();
        });

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd && this.platformService.isBrowser),
                untilDestroyed(this)
            )
            .subscribe(() => this.removeAllNotifications());
    }

    public removeNotification(notification: NotificationToast): void {
        const index = this.notifications.findIndex(n => n.id === notification.id);
        if (index === -1) {
            return;
        }
        if (notification.onClose) {
            notification.onClose();
        }
        this.notifications.splice(index, 1);
        this.cd.markForCheck();
    }

    private removeAllNotifications(): void {
        this.notifications = [];
    }
}
